import { RequireAccess } from '@/components/require-access'
import { useJobillaOrgId } from '@/hooks'
import { useAuth } from '@/lib'
import { CampaignStatusBadge } from '@/ui/constructs/campaign/campaign-status-badge'
import { CogMenu } from '@/ui/rapid-ui/cog-menu'
import { Glyphs } from '@/ui/rapid-ui/glyphs'
import { ConfirmationDialog } from '@/ui/rapid-ui/structural/confirmation-dialog'
import type { ConfirmationDialogRef } from '@/ui/rapid-ui/structural/confirmation-dialog'
import { Tooltip } from '@/ui/rapid-ui/tooltip'
import { imgixUrl } from '@/ui/utilities/imgix'
import { logger } from '@/ui/utilities/instrumentation/logger'
import { NoOrphan } from '@/ui/utilities/no-orphan'
import { useOrganisationRelationShow } from '@jobilla/nexus-sdk'
import ctl from '@netlify/classnames-template-literals'
import { differenceInDays } from 'date-fns'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ActiveContent } from './active-content'
import { CampaignAccess } from './campaign-access'
import { CampaignCardImagePlaceholder } from './campaign-card-image-placeholder'
import { campaignMenuActions, hasAnyCampaignMenuAction } from './campaign-menu-actions'
import { CtaButton } from './cta-button'
import { BudgetWithBreakdown } from './dashboard-helpers'
import type { DashboardCampaignModel, DashboardViewModel } from './dashboard.vm'

type CampaignTileProps = {
	campaign: DashboardCampaignModel
	i: number
	linkRenderer: DashboardViewModel['renderers']['campaignLink']
	onArchive: DashboardViewModel['events']['onArchiveCampaign']
	onUnarchive: DashboardViewModel['events']['onUnarchiveCampaign']
	onCopy: DashboardViewModel['events']['onCopyCampaign']
	onDelete?: (id: string) => void
	isArchiving?: boolean
	isUnarchiving?: boolean
	isCopying?: boolean
}

export function CampaignDashboardCard(props: CampaignTileProps) {
	const { t } = useTranslation()
	const organisationId = useJobillaOrgId()
	const { data: orgData } = useOrganisationRelationShow(organisationId)
	const { can } = useAuth()
	const aclEnabled = orgData?.accessMode === 'explicit'
	const canManage = can('campaign-access:update')

	const cardImageUrl = props.campaign.status === 'draft'
		? imgixUrl(props.campaign.image, { w: 400, h: 500, fit: 'crop', dpr: 2 })
		: props.campaign.image
	const [failedImageUrl, setFailedImageUrl] = useState<string | null>(null)
	const showCardImage = Boolean(cardImageUrl) && cardImageUrl !== failedImageUrl
	const cardImageAlt = props.campaign.title || t('dashboard.card.imageAlt')

	const [hasHovered, setHasHovered] = useState(false)
	const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const archiveConfirmationRef = useRef<ConfirmationDialogRef>(null)
	const unarchiveConfirmationRef = useRef<ConfirmationDialogRef>(null)

	const isFormOpen = props.campaign.formClosedAt === null || new Date(props.campaign.formClosedAt) > new Date()

	const handleArchiveClick = async () => {
		try {
			await archiveConfirmationRef.current?.confirm({
				title: t('dashboard.card.archiveTitle'),
				message: isFormOpen
					? t('dashboard.card.archiveMessageWithOpenForm', {
						name: props.campaign.title || t('dashboard.card.unnamedCampaign'),
					})
					: t('dashboard.card.archiveMessage', {
						name: props.campaign.title || t('dashboard.card.unnamedCampaign'),
					}),
				confirmText: t('dashboard.card.confirmArchive'),
				cancelText: t('dashboard.card.cancel'),
			})
			await props.onArchive(props.campaign.id, props.campaign.title)
		} catch {
			// cancelled
		}
	}

	const handleUnarchiveClick = async () => {
		try {
			await unarchiveConfirmationRef.current?.confirm({
				title: t('dashboard.card.unarchiveTitle'),
				message: t('dashboard.card.unarchiveMessage', {
					name: props.campaign.title || t('dashboard.card.unnamedCampaign'),
				}),
				confirmText: t('dashboard.card.confirmUnarchive'),
				cancelText: t('dashboard.card.cancel'),
			})
			await props.onUnarchive(props.campaign.id, props.campaign.title)
		} catch {
			// cancelled
		}
	}

	const handleCopyClick = async () => {
		await props.onCopy(props.campaign.id, props.campaign.title)
	}

	const menuActions = campaignMenuActions(props.campaign, can)

	const { campaign, linkRenderer } = props
	const isEndingSoon = campaign.status === 'active' && campaign.endsAt != null
		&& differenceInDays(campaign.endsAt, new Date()) <= 3
	const isActiveOrEndingSoon = campaign.status === 'active'

	const ctaLabel = campaign.status === 'draft'
		? t('dashboard.card.cta.continueDraft')
		: campaign.status === 'review' || campaign.status === 'publishing'
		? t('dashboard.card.cta.learnMore')
		: t('dashboard.card.cta.viewCandidates')

	const handleCogOpen = () => {
		logger.track('Dashboard: Cog menu opened', {
			data: { campaign_id: campaign.id, campaign_status: campaign.status },
		})
	}

	return (
		<div
			className={ctl(`
				w-full rounded-2xl shadow-md transition-all duration-200
				${props.isArchiving || props.isUnarchiving || props.isCopying ? 'opacity-40 pointer-events-none' : ''}
			`)}
			onMouseEnter={() => {
				if (!hasHovered && !hoverTimerRef.current) {
					hoverTimerRef.current = setTimeout(() => setHasHovered(true), 200)
				}
			}}
			onMouseLeave={() => {
				if (hoverTimerRef.current) {
					clearTimeout(hoverTimerRef.current)
					hoverTimerRef.current = null
				}
			}}
		>
			<div className="w-full h-full rounded-2xl overflow-hidden flex flex-col bg-white">
				{linkRenderer(campaign.id, campaign.status, campaign.isArchived, {
					className: 'block',
					children: (
						<div className="w-full flex relative overflow-hidden aspect-[4/5]">
							{showCardImage
								? (
									<>
										<div
											className="absolute inset-0 blur-sm bg-cover bg-no-repeat bg-center brightness-40"
											style={{ backgroundImage: `url(${cardImageUrl})` }}
										/>

										<img
											className="relative self-center"
											src={cardImageUrl}
											alt={cardImageAlt}
											onError={() => setFailedImageUrl(cardImageUrl)}
										/>
									</>
								)
								: <CampaignCardImagePlaceholder branding={campaign.branding} alt={cardImageAlt} />}
						</div>
					),
				})}

				<div className="px-3 pt-4 pb-3.5">
					<CampaignStatusBadge
						status={campaign.status}
						startedAt={campaign.startedAt}
						endsAt={campaign.endsAt}
						updatedAt={campaign.updatedAt}
						isEvergreen={campaign.isEvergreen}
					/>
				</div>

				<div
					className="text-grey-900 text-xl font-semibold truncate px-4"
					title={campaign.title}
				>
					<NoOrphan>{campaign.title}</NoOrphan>
				</div>
				<div className="text-grey-600 text-sm mt-1 flex items-center gap-1 px-4">
					<span className="size-2.5 shrink-0 text-grey-400">
						<Glyphs.micro.PinIcon />
					</span>
					<span className="truncate">{campaign.location}</span>
				</div>

				{(isActiveOrEndingSoon || campaign.status === 'completed')
					? (
						<div className="px-4 pt-4 mt-4 border-t border-gray-100">
							<ActiveContent
								campaign={campaign}
								hireCount={campaign.status === 'completed' ? campaign.hireCount : null}
							/>
						</div>
					)
					: (
						<div className="flex-1 px-4 pt-4 mt-4 border-t border-gray-100">
						</div>
					)}

				<div className="p-4 flex items-center gap-2 text-xs text-grey-600 mt-4 border-t border-gray-100">
					{!campaign.isEvergreen
						? (
							<RequireAccess permission="campaign:view-budget" fallback={<div className={'h-4'}></div>}>
								<BudgetWithBreakdown
									budget={campaign.budget}
									budgetBreakdown={campaign.budgetBreakdown}
									className="font-medium text-grey-700"
								/>
							</RequireAccess>
						)
						: <div className={'h-4'}></div>}
					{(aclEnabled || canManage) && (
						<>
							<span className="text-grey-300">·</span>
							<CampaignAccess
								campaignId={campaign.id}
								prefetch={hasHovered}
								source="dashboard"
								accessors={campaign.accessors}
							/>
						</>
					)}
				</div>

				<div className="flex">
					{hasAnyCampaignMenuAction(menuActions) && (
						<CogMenu
							className="text-grey-500 shrink-0 px-4 flex items-center bg-purple-100 hover:bg-purple-200"
							onOpen={handleCogOpen}
							actions={[
								...(menuActions.clone
									? [{
										id: 'clone',
										label: t('dashboard.card.action.clone'),
										icon: <Glyphs.core.CopyIcon />,
										onClick: handleCopyClick,
									}]
									: []),
								...(menuActions.archive
									? [{
										id: 'archive',
										label: t('dashboard.card.action.archive'),
										icon: <Glyphs.core.ArchiveIcon />,
										onClick: handleArchiveClick,
									}]
									: []),
								...(menuActions.unarchive
									? [{
										id: 'unarchive',
										label: t('dashboard.card.action.unarchive'),
										icon: <Glyphs.core.ArchiveIcon />,
										onClick: handleUnarchiveClick,
									}]
									: []),
							]}
						/>
					)}
					<div className="flex-1 min-w-0">
						{campaign.status === 'review' || campaign.status === 'publishing'
							? (
								<Tooltip
									content={t('dashboard.card.reviewTooltip')}
									wrapperClassName="w-full"
									interactive
									className="max-w-64 leading-relaxed"
								>
									<CtaButton
										linkRenderer={linkRenderer}
										campaign={campaign}
										label={ctaLabel}
									/>
								</Tooltip>
							)
							: (
								<CtaButton
									linkRenderer={linkRenderer}
									campaign={campaign}
									label={ctaLabel}
								/>
							)}
					</div>
				</div>
			</div>

			<ConfirmationDialog ref={archiveConfirmationRef} />
			<ConfirmationDialog ref={unarchiveConfirmationRef} />
		</div>
	)
}
